import React, { useEffect, useState } from 'react';
import { Avatar, Badge, Drawer, Spin, Typography, theme } from 'antd';
import { AnimatePresence, motion } from 'framer-motion';
import { message } from '../../services/toastMessage';
import { IconMessageOff, IconChevronRight, IconHeadset } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import dayjs, { Dayjs } from 'dayjs';
import AppLayout from '../../components/Layouts/Layout';
import EmptyState from '../../components/EmptyState';
import MessageThreadPanel from '../../components/MessageThreadPanel';
import BookingDetailsPanel from '../../components/BookingDetailsPanel';
import AddressMapModal from '../../components/AddressMapModal';
import ConfirmBottomSheet from '../../components/ConfirmBottomSheet';
import SwipeToDeleteRow from '../../components/SwipeToDeleteRow';
import HostCancelBookingModal from '../../components/HostCancelBookingModal';
import ProposeNewQuoteAmountModal from '../../components/ProposeNewQuoteAmountModal';
import RejectQuoteModal from '../../components/RejectQuoteModal';
import ResumePaymentModal from '../../components/ResumePaymentModal';
import { getMyConversations, hideConversation } from '../../services/ConversationService';
import { getMyAdminConversation } from '../../services/AdminConversationService';
import { IAdminConversationSummary } from '../../interfaces/heavyDutyMachinery/IAdminConversation';
import { onReceiveMessage, onReceiveNotification } from '../../services/RealtimeService';
import { getBookingById, cancelBooking, acceptQuoteBooking, cancelQuoteBooking } from '../../services/BookingService';
import { useAuth } from '../../contextProviders/AuthProvider';
import { canOfferCancel } from '../../helpers/bookingHelpers';
import { isDailyRentalMode, lastRentalDay } from '../../helpers/rentalPeriodHelper';
import { useAppBreakpoint } from '../../hooks/useAppBreakpoint';
import { getLocalStorageKey, setLocalStorageKey } from '../../helpers/localStorageHelper';
import { IConversationSummary } from '../../interfaces/heavyDutyMachinery/IConversation';
import { BookingStatus, IBooking } from '../../interfaces/heavyDutyMachinery/IBooking';
import { BOOKING_CHANGED_NOTIFICATION_TYPES } from '../../interfaces/heavyDutyMachinery/INotification';
import {
  BOOKING_STATUS_ICON,
  BOOKING_STATUS_LABEL_KEYS,
  NEGATIVE_BOOKING_STATUSES,
  POSITIVE_BOOKING_STATUSES,
} from '../../constants/bookingStatusOptions';
import {
  SERVICE_REQUEST_STATUS_ICON,
  SERVICE_REQUEST_STATUS_LABEL_KEYS,
  SERVICE_REQUEST_STATUS_TONES,
} from '../../constants/serviceRequestOptions';
import { ServiceRequestStatus } from '../../interfaces/heavyDutyMachinery/IServiceRequest';
import { pillToneStyle } from '../../theme/creamyTokens';
import { TEAL, TEAL_SOFT_BG, CORAL, CORAL_SOFT_BG } from '../../theme/creamyTokens';
import { TRANSLATIONS } from '../../constants/translations';
import { translate } from '../../helpers/translationHelper';

/**
 * What a conversation row is headlined with.
 *
 * The person, not the machine: a thread is a conversation with somebody, and
 * "Juan Pérez" is how you find it again. Falls back to the machine title
 * rather than ever rendering empty -- peerDisplayName can genuinely come
 * back blank (see ConversationSummaryResponse.PeerDisplayName's own doc
 * comment, and the UserPublicIdentity backfill gap that left older profiles
 * without one), and a nameless row is exactly the bug this replaced.
 */
const conversationTitle = (conversation: IConversationSummary): string =>
  conversation.peerDisplayName?.trim() || conversation.heavyDutyMachineryTitle || '';

/**
 * The second line: which rental this thread is about. A name alone stops
 * being enough the moment the same customer rents twice, so the machine and
 * the dates disambiguate it.
 *
 * A by-days booking's endAt is exclusive, so the last day shown is the day
 * before it (see rentalPeriodHelper) -- printing endAt raw would name a day
 * nobody rents. Dates are dropped entirely when the backend hasn't sent them
 * (see IConversationSummary.startAt), leaving just the machine.
 *
 * The booking reference leads, because it is the one part of this line that
 * is unique: two threads can share a machine and a customer, and comparing
 * two date ranges to tell them apart is work the reference does in six
 * characters.
 */
const conversationSubtitle = (conversation: IConversationSummary, language: string): string => {
  const reference = conversation.bookingReference;
  const machinery = [reference, conversation.heavyDutyMachineryTitle].filter(Boolean).join(' · ');
  if (!conversation.startAt || !conversation.endAt) return machinery;
  const start = dayjs(conversation.startAt);
  const end = conversation.rentalMode && isDailyRentalMode(conversation.rentalMode)
    ? lastRentalDay(conversation.endAt)
    : dayjs(conversation.endAt);
  const dates = start.isSame(end, 'day')
    ? formatShortDate(start, language)
    : `${formatShortDate(start, language)} – ${formatShortDate(end, language)}`;
  return machinery ? `${machinery} · ${dates}` : dates;
};

/** Day + abbreviated month, no year -- a booking list is always near-term. */
const formatShortDate = (date: Dayjs, language: string): string =>
  date.locale(language).format('D MMM');

const { Text } = Typography;

const LIST_WIDTH_STORAGE_KEY = 'heavyDutyMachineryMessagesListWidth';
const LIST_WIDTH_MIN = 200;
const LIST_WIDTH_MAX = 400;
const LIST_WIDTH_DEFAULT = 220;

const clampListWidth = (width: number) => Math.min(LIST_WIDTH_MAX, Math.max(LIST_WIDTH_MIN, width));

// How close to the list's own bottom edge (px) triggers loading the next
// page -- far enough ahead that the next page is usually already in by the
// time the user's eye reaches the very last loaded row.
const LOAD_MORE_THRESHOLD_PX = 200;

// Same teal-for-positive criterion as MessageThreadPanel's own status card
// (statusCardStyle there); Cancelled/no-show get antd's own error tokens --
// the same danger tone this app's error toasts and MessageThreadPanel's own
// "Cancelar reserva" pill already use -- rather than the neutral treatment
// that card still gives those statuses today (see
// NEGATIVE_BOOKING_STATUSES' own doc comment in bookingStatusOptions.ts).
const bookingStatusBadgeColors = (
  status: BookingStatus,
  token: ReturnType<typeof theme.useToken>['token']
): { bg: string; fg: string } => {
  if (POSITIVE_BOOKING_STATUSES.includes(status)) return { bg: TEAL_SOFT_BG, fg: TEAL };
  if (NEGATIVE_BOOKING_STATUSES.includes(status)) return { bg: token.colorErrorBg, fg: token.colorError };
  return { bg: token.colorFillTertiary, fg: token.colorTextSecondary };
};

/**
 * The little round status badge, for either kind of thread.
 *
 * A quote-request conversation carries no booking status at all -- reading one
 * off it gives `undefined` for the icon, and rendering `<undefined />` is how
 * React takes a whole screen down. So the kind decides which vocabulary the
 * badge speaks, rather than one of them being assumed.
 */
const conversationStatusBadge = (
  conversation: IConversationSummary,
  token: ReturnType<typeof theme.useToken>['token']
): { Icon: React.ElementType; labelKey: string; bg: string; fg: string } => {
  if (conversation.kind === 'ServiceRequest') {
    const status = conversation.serviceRequestStatus as ServiceRequestStatus;
    const tone = pillToneStyle(SERVICE_REQUEST_STATUS_TONES[status], token);
    return {
      Icon: SERVICE_REQUEST_STATUS_ICON[status],
      labelKey: SERVICE_REQUEST_STATUS_LABEL_KEYS[status],
      bg: String(tone.background),
      fg: String(tone.color),
    };
  }
  return {
    Icon: BOOKING_STATUS_ICON[conversation.bookingStatus],
    labelKey: BOOKING_STATUS_LABEL_KEYS[conversation.bookingStatus],
    ...bookingStatusBadgeColors(conversation.bookingStatus, token),
  };
};

// AppHeader's own fixed height (see Layout.tsx's identical `calc(100dvh -
// 64px)` / `marginTop: 64`).
const APP_HEADER_HEIGHT = 64;

// The `dvh` CSS unit accounts for browser chrome (Safari's address bar
// showing/hiding) but NOT the iOS software keyboard -- a box sized with
// `calc(100dvh - 64px)` doesn't shrink when the keyboard opens, so this
// page's own pinned input row (see MessageThreadPanel) ends up laid out
// below the area that's actually still visible, under the keyboard.
// `window.visualViewport` DOES shrink/pan correctly for the keyboard on
// iOS, so this hook tracks that instead wherever it's available (falls back
// to `window.innerHeight`/no pan, e.g. on desktop or older browsers, where
// there's no keyboard to worry about in the first place).
interface ViewportInfo {
  height: number;
  offsetTop: number;
}

const useViewportHeight = (): ViewportInfo => {
  const [info, setInfo] = useState<ViewportInfo>(() => ({
    height: window.visualViewport?.height ?? window.innerHeight,
    offsetTop: window.visualViewport?.offsetTop ?? 0,
  }));

  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) return undefined;
    const update = () => setInfo({ height: viewport.height, offsetTop: viewport.offsetTop });
    update();
    viewport.addEventListener('resize', update);
    viewport.addEventListener('scroll', update);
    return () => {
      viewport.removeEventListener('resize', update);
      viewport.removeEventListener('scroll', update);
    };
  }, []);

  return info;
};

const loadStoredListWidth = (): number => {
  const stored = getLocalStorageKey(LIST_WIDTH_STORAGE_KEY);
  const parsed = stored ? parseInt(stored, 10) : NaN;
  return Number.isFinite(parsed) ? clampListWidth(parsed) : LIST_WIDTH_DEFAULT;
};

/**
 * Three-panel Airbnb-style inbox: conversation list (left, resizable) +
 * message thread (center) + booking details (right, collapsible, fixed
 * width). Every Conversation is tied to exactly one Booking by the current
 * data model, so the details panel always has something to show once a
 * conversation is selected.
 */
const Messages: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { token } = theme.useToken();
  const { userId } = useAuth();
  const navigate = useNavigate();
  const { isMobile, isDesktop } = useAppBreakpoint();
  const viewportInfo = useViewportHeight();
  const viewportHeight = viewportInfo.height;
  // The iOS keyboard pans visualViewport down rather than shrinking the
  // layout viewport, scrolling the fixed AppHeader out of view once the pan
  // exceeds its height -- so the box is pinned (`position: fixed` below) to
  // whichever is lower, the header's bottom edge or the viewport's current
  // top, and sized to reach the visible window's actual bottom edge.
  const visibleBottom = viewportInfo.offsetTop + viewportHeight;
  const containerTop = Math.max(APP_HEADER_HEIGHT, viewportInfo.offsetTop);
  const containerHeight = visibleBottom - containerTop;
  const [searchParams, setSearchParams] = useSearchParams();
  const [conversations, setConversations] = useState<IConversationSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const [selectedConversation, setSelectedConversation] = useState<IConversationSummary | undefined>(undefined);
  const [selectedBooking, setSelectedBooking] = useState<IBooking | undefined>(undefined);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [detailsPanelOpen, setDetailsPanelOpen] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [acceptingQuote, setAcceptingQuote] = useState(false);
  const [cancellingQuote, setCancellingQuote] = useState(false);
  // Host-side counter/reject, opened from the breakdown sheet inside the
  // thread. Both reuse the modals IncomingBookings already uses, so the two
  // entry points can't drift in what they ask for.
  const [proposeQuoteOpen, setProposeQuoteOpen] = useState(false);
  // Host-only, and only once a quote is accepted: see
  // MessageThreadPanelProps.onHostCancelBooking for why their side of that
  // state would otherwise have no action at all.
  const [hostCancelOpen, setHostCancelOpen] = useState(false);
  const [rejectQuoteOpen, setRejectQuoteOpen] = useState(false);
  // Guest-side, AwaitingPayment: the real Stripe step, reusing the same modal
  // MyBookings already opens for an unfinished payment.
  const [payNowOpen, setPayNowOpen] = useState(false);
  const [addressModalOpen, setAddressModalOpen] = useState(false);

  const [listWidth, setListWidth] = useState(loadStoredListWidth);
  const [resizerHover, setResizerHover] = useState(false);

  // Mobile swipe-to-delete: at most one row's delete action revealed at a
  // time (opening another row's closes whichever was open, see the row's
  // own isOpen prop below); hideTarget drives the ConfirmBottomSheet.
  const [openSwipeId, setOpenSwipeId] = useState<string | null>(null);
  const [hideTarget, setHideTarget] = useState<IConversationSummary | undefined>(undefined);

  // Doubles as the entry point for /heavy-duty-machinery/support itself -- there's no
  // separate persistent nav entry point to it, so this banner (and the
  // NotificationBell's own AdminMessage link) is how a guest/host reaches
  // it, whether that's "contactar con soporte" for the first time or coming
  // back to an existing thread. undefined = not loaded yet (hidden, avoids
  // flashing the wrong wording before this resolves); null = confirmed no
  // conversation has ever existed, so the banner is the plain "contact us"
  // invitation; a real summary once one exists, whose unreadCount decides
  // whether the wording/color claims something new is waiting or reads as a
  // calm link back to the history. Silent on failure -- this is a "nice to
  // have if present" check, never worth a toast of its own on an
  // already-loading inbox.
  const [adminConversation, setAdminConversation] = useState<IAdminConversationSummary | null | undefined>(undefined);
  useEffect(() => {
    getMyAdminConversation()
      .then(setAdminConversation)
      .catch(() => undefined);
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const targetConversationId = searchParams.get('conversationId');
      // Set right after a fresh booking (HeavyDutyMachineryBookingWidget navigates
      // here with only the bookingId it just got back -- the Conversation
      // it opened server-side in the same request has no id to link to yet).
      const targetBookingId = searchParams.get('bookingId');
      const hasTarget = !!targetConversationId || !!targetBookingId;

      // Loads pages one at a time, same cursor loadMore below uses --
      // stops after the first page unless chasing a specific deep-linked
      // conversation/booking (e.g. a notification-bell link) that isn't on
      // it, in which case it keeps paging until found or genuinely
      // exhausted. That deep link used to always work (everything loaded
      // at once); this keeps it working without giving up pagination for
      // the common case, where there's no target or it's recent enough to
      // already be on page 1.
      let accumulated: IConversationSummary[] = [];
      let cursor: string | undefined;
      let more = true;
      let match: IConversationSummary | undefined;
      do {
        const page = await getMyConversations(cursor);
        accumulated = accumulated.concat(page.items);
        more = page.hasMore;
        cursor = page.items.length > 0 ? page.items[page.items.length - 1].id : undefined;
        if (hasTarget) match = accumulated.find((c) => c.id === targetConversationId || c.bookingId === targetBookingId);
      } while (hasTarget && !match && more);

      setConversations(accumulated);
      setHasMore(more);

      // No specific conversation/booking requested via the URL -- default
      // to the most recent one (list is already sorted by LastMessageAt
      // descending server-side, see ConversationsController) instead of
      // landing on the empty "select a conversation" prompt. Desktop only:
      // mobile has no permanent list column to fall back to, so jumping
      // straight into a thread would skip the list ("screen 1") the user
      // actually tapped "Mensajes" to reach -- mobile lands there instead
      // and only opens a thread when explicitly requested via the URL.
      const defaultSelection = match ?? (!isMobile && !hasTarget ? accumulated[0] : undefined);
      if (defaultSelection) handleSelectConversation(defaultSelection);
    } catch {
      message.error(translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_MESSAGES_LOAD_ERROR));
    } finally {
      setLoading(false);
    }
  };

  // Infinite scroll's own next-page fetch (see handleListScroll below) --
  // guarded on loadingMore so a fast scroll firing several times while a
  // page is already in flight doesn't queue up duplicate requests.
  const loadMore = async () => {
    if (loadingMore || !hasMore || conversations.length === 0) return;
    setLoadingMore(true);
    try {
      const cursor = conversations[conversations.length - 1].id;
      const page = await getMyConversations(cursor);
      setConversations((prev) => [...prev, ...page.items]);
      setHasMore(page.hasMore);
    } catch {
      message.error(translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_MESSAGES_LOAD_ERROR));
    } finally {
      setLoadingMore(false);
    }
  };

  const handleListScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < LOAD_MORE_THRESHOLD_PX) loadMore();
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Live push for the LIST itself -- distinct from MessageThreadPanel's own
  // onReceiveMessage (which only fires while that one thread is mounted),
  // this keeps a conversation row's badge/last-message/position current
  // while the user is sitting on the list, on both mobile and desktop
  // (same shared `conversations` state either way). incoming.read already
  // encodes whether the backend considered the recipient to be actively
  // viewing that thread at send time (see HeavyDutyMachineryHub's
  // IActiveConversationTracker, checked in ConversationsController.
  // SendMessage) -- true there means it's the currently-open conversation,
  // so this only ever bumps unreadCount when it's actually false, matching
  // the same "open thread doesn't count as unread" rule the bell
  // notifications already follow.
  useEffect(() => {
    return onReceiveMessage((incoming) => {
      setConversations((prev) => {
        const index = prev.findIndex((c) => c.id === incoming.conversationId);
        // Not a conversation this page has loaded yet (e.g. further down a
        // page the user hasn't scrolled to) -- nothing in the visible list
        // to update, the next load/loadMore will bring it in with the
        // right count already baked in server-side.
        if (index === -1) return prev;
        const updated: IConversationSummary = {
          ...prev[index],
          lastMessageText: incoming.text,
          lastMessageAt: incoming.sentAt,
          unreadCount: incoming.read ? prev[index].unreadCount : prev[index].unreadCount + 1,
        };
        const rest = prev.filter((_, i) => i !== index);
        return [updated, ...rest];
      });
    });
  }, []);

  // Live push for the BOOKING behind the open thread. Without this the panel
  // showed whatever the booking looked like the moment it was opened: a guest
  // sitting in the thread while the host accepted their quote kept being
  // offered "ver presupuesto" and never grew a "pagar ahora" button, because
  // nothing on this screen had any reason to believe the booking had moved.
  //
  // Refetched rather than patched from the notification: these pushes carry a
  // title and a link, not a booking, and the panel branches on status,
  // payment status, quote revisions and deadlines at once. Re-reading the row
  // is one request and cannot drift from the server the way a hand-merged
  // partial would.
  //
  // Scoped to the thread that is actually open -- a notification about some
  // other booking has nothing to update here, and the list's own live channel
  // (onReceiveMessage above) already keeps the rows current. Read through the
  // setter so the handler never closes over a stale selectedBooking.
  useEffect(() => {
    return onReceiveNotification((notification) => {
      if (!BOOKING_CHANGED_NOTIFICATION_TYPES.includes(notification.type)) return;
      setSelectedBooking((current) => {
        if (current) {
          getBookingById(current.id)
            .then(setSelectedBooking)
            // A refresh that fails leaves the panel on the state it already
            // had, which is what it was showing a moment ago -- no worse than
            // before, and never a blank card.
            .catch(() => undefined);
        }
        return current;
      });
    });
  }, []);

  const handleSelectConversation = (conversation: IConversationSummary) => {
    setSelectedConversation(conversation);
    setSelectedBooking(undefined);
    // Desktop keeps its permanent third column open by default, same as
    // before -- tablet/mobile show details on demand only (see
    // MessageThreadPanel's header toggle), never stealing a fixed column.
    setDetailsPanelOpen(isDesktop);
    // Mirrors the selection into the URL (same query param the initial
    // deep-link match below already reads) so a mobile refresh mid-thread
    // lands back on the thread screen instead of the conversation list.
    setSearchParams({ conversationId: conversation.id });

    // A quote-request thread has no booking, and asking for one was the bug:
    // the fetch failed, selectedBooking stayed undefined, and the thread sat
    // behind a spinner that never went away. A chat needs its conversation,
    // not a booking.
    if (!conversation.bookingId) {
      setBookingLoading(false);
      return;
    }

    setBookingLoading(true);
    getBookingById(conversation.bookingId)
      .then(setSelectedBooking)
      .catch(() => message.error(translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_MY_BOOKINGS_LOAD_ERROR)))
      .finally(() => setBookingLoading(false));
  };

  // Fired by MessageThreadPanel the moment its own GET call marks this
  // conversation's messages Read server-side -- zeroes the badge in the
  // list's own state immediately, instead of it only catching up on the
  // next full reload (the bug this fixes: opening a thread already clears
  // it server-side, but `conversations` here had no way to find out until
  // now). Only ever lowers a count that's already stale, never bumps one up
  // -- a genuinely new message arriving while this conversation isn't the
  // one open still comes through the unrelated live-push path elsewhere,
  // untouched by this.
  const handleMessagesRead = (conversationId: string) => {
    setConversations((prev) => prev.map((c) => (c.id === conversationId ? { ...c, unreadCount: 0 } : c)));
  };

  // Mobile-only: the thread is a full-screen "screen" navigated to from the
  // conversation list rather than a permanent column, so "back" means
  // returning to that list, not leaving the page.
  const handleBackToList = () => {
    setSelectedConversation(undefined);
    setSelectedBooking(undefined);
    setDetailsPanelOpen(false);
    setSearchParams({});
  };

  // The one and only cancelBooking call for this page -- both the thread's
  // quick-action button and the details panel's own button call this same
  // handler, they never duplicate the request themselves.
  const handleCancelBooking = async () => {
    if (!selectedBooking) return;
    setCancelling(true);
    try {
      const updated = await cancelBooking(selectedBooking.id);
      setSelectedBooking(updated);
      message.success(translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_CANCEL_SUCCESS));
    } catch (error: any) {
      message.error(error.message || translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_CANCEL_ERROR));
    } finally {
      setCancelling(false);
    }
  };

  // Same "one call, both the thread's quick action and any other caller
  // share it" reasoning as handleCancelBooking above -- reserva diferida's
  // own guest-side actions, only ever offered while selectedBooking is
  // AwaitingQuote/AwaitingPayment (see MessageThreadPanelProps.onAcceptQuote's
  // own doc comment).
  const handleAcceptQuote = async () => {
    if (!selectedBooking) return;
    setAcceptingQuote(true);
    try {
      const updated = await acceptQuoteBooking(selectedBooking.id);
      setSelectedBooking(updated);
      message.success(translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_QUOTE_ACCEPT_SUCCESS));
    } catch (error: any) {
      message.error(error.message || translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_QUOTE_ACCEPT_ERROR));
    } finally {
      setAcceptingQuote(false);
    }
  };

  const handleCancelQuote = async () => {
    if (!selectedBooking) return;
    setCancellingQuote(true);
    try {
      const updated = await cancelQuoteBooking(selectedBooking.id);
      setSelectedBooking(updated);
      message.success(translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_CANCEL_QUOTE_SUCCESS));
    } catch (error: any) {
      message.error(error.message || translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_CANCEL_QUOTE_ERROR));
    } finally {
      setCancellingQuote(false);
    }
  };

  // Optimistic: the row disappears (animated by the AnimatePresence/
  // motion.div wrapping each row below) the instant the sheet is confirmed,
  // before the backend call resolves -- if it fails, the conversation is
  // spliced back into its original position and a toast explains why. Never
  // a real delete server-side (see hideConversation's own doc comment) --
  // just this participant's own view of the list.
  const handleConfirmHide = async () => {
    if (!hideTarget) return;
    const target = hideTarget;
    const originalIndex = conversations.findIndex((c) => c.id === target.id);
    setHideTarget(undefined);
    setConversations((prev) => prev.filter((c) => c.id !== target.id));
    try {
      await hideConversation(target.id);
    } catch {
      setConversations((prev) => {
        const next = [...prev];
        next.splice(Math.min(originalIndex, next.length), 0, target);
        return next;
      });
      message.error(translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_MESSAGES_HIDE_ERROR));
    }
  };

  // Desktop/tablet only: drags the divider between the conversation list
  // and the thread, clamped to [LIST_WIDTH_MIN, LIST_WIDTH_MAX] so neither
  // panel can be squeezed into an unusable width. The booking-details
  // column on the right is deliberately never wired to this -- it stays a
  // fixed width regardless.
  const handleResizerMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = listWidth;
    let nextWidth = startWidth;
    const onMouseMove = (moveEvent: MouseEvent) => {
      nextWidth = clampListWidth(startWidth + (moveEvent.clientX - startX));
      setListWidth(nextWidth);
    };
    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      setLocalStorageKey(LIST_WIDTH_STORAGE_KEY, String(nextWidth));
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  const canCancelSelected = !!selectedBooking && selectedBooking.guestUserId === userId && canOfferCancel(selectedBooking);
  // Which side of the negotiation the viewer is on. The host now decides
  // from inside this thread too, so the guest-only actions (cancel the
  // request) and the host-only ones (counter, reject) are split by this
  // rather than by which page the panel happens to be rendered on.
  const isGuestOfSelected = !!selectedBooking && selectedBooking.guestUserId === userId;

  // Mobile only: an open thread is its own full-screen "screen 2" (see
  // handleSelectConversation/handleBackToList above) -- the bottom tab bar
  // has no business staying on screen there. It used to stay mounted the
  // whole time, which meant it sat (at a higher z-index) directly on top of
  // this page's own keyboard-pinned input row once the keyboard opened,
  // same industry pattern as WhatsApp/Instagram/Telegram hiding their own
  // tab bar the moment a chat opens.
  const hideBottomNavForThread = isMobile && !!selectedConversation;

  return (
    <AppLayout hideFooter hideBottomNav={hideBottomNavForThread}>
      {/* `position: fixed` (rather than filling Content's normal-flow box
          with a negative-margin breakout) so this module tracks the real
          visible window directly via containerTop/containerHeight above,
          instead of assuming a fixed offset from a layout viewport that iOS
          doesn't resize for the keyboard. */}
      <div
        style={{
          position: 'fixed',
          top: containerTop,
          left: 0,
          width: '100%',
          height: containerHeight,
          overflow: 'hidden',
          background: token.colorBgContainer,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Always shown once loaded -- doubles as "Contactar con soporte"
            (adminConversation === null) and as the way back to an existing
            thread. Wording/color only claim "you have a message" while
            there's an actual unread one -- otherwise (no conversation yet,
            or read/closed with nothing new) it reads as a plain,
            non-alarming link so it never implies something new is waiting
            when there isn't. */}
        {adminConversation !== undefined && (
          <button
            type="button"
            onClick={() => navigate('/heavy-duty-machinery/support')}
            style={{
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              width: '100%',
              padding: '10px 16px',
              border: 'none',
              borderBottom: `1px solid ${token.colorBorderSecondary}`,
              background: adminConversation && adminConversation.unreadCount > 0 ? CORAL_SOFT_BG : token.colorFillTertiary,
              cursor: 'pointer',
              textAlign: 'left',
            }}
          >
            <IconHeadset size={16} color={adminConversation && adminConversation.unreadCount > 0 ? CORAL : undefined} />
            <Text style={{ fontSize: 13, flex: 1, color: adminConversation && adminConversation.unreadCount > 0 ? CORAL : undefined }}>
              {translate(
                t,
                !adminConversation
                  ? TRANSLATIONS.HEAVY_DUTY_MACHINERY_SUPPORT_BANNER_TEXT_START
                  : adminConversation.unreadCount > 0
                  ? TRANSLATIONS.HEAVY_DUTY_MACHINERY_SUPPORT_BANNER_TEXT
                  : TRANSLATIONS.HEAVY_DUTY_MACHINERY_SUPPORT_BANNER_TEXT_READ
              )}
            </Text>
            <IconChevronRight size={14} color={token.colorTextTertiary} />
          </button>
        )}
        <div style={{ flex: 1, minHeight: 0 }}>
        {loading ? (
          <div style={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center' }}>
            <Spin size="large" />
          </div>
        ) : conversations.length === 0 ? (
          <div style={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center' }}>
            <EmptyState
              icon={<IconMessageOff size={24} />}
              title={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_MESSAGES_EMPTY)}
              description={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_MESSAGES_EMPTY_DESCRIPTION)}
            />
          </div>
        ) : (
          <div style={{ display: 'flex', height: '100%', minHeight: 0 }}>
            {/* Mobile: the list is its own full-width "screen 1", swapped
                out for the thread ("screen 2") the moment a conversation is
                selected -- never stacked/overlapping. Tablet/desktop keep
                it as a permanent left column, exactly as before.
                overscrollBehavior: 'contain' (here and on the thread's own
                message body, see MessageThreadPanel) stops the browser's
                mouse-wheel scroll "latching" -- once a wheel gesture starts
                over a scrollable element, browsers keep routing further
                wheel events to that SAME element even after it's hit its
                boundary, until the gesture goes idle or the cursor moves
                to a genuinely different scrollable area. Without this,
                fast wheel scrolling near the top/bottom edge feels like it
                randomly "sticks" for a beat before releasing -- a desktop-
                mouse-only quirk, touch scrolling on tablet/phone never hits
                this since it has no such latching. */}
            {(!isMobile || !selectedConversation) && (
              <div
                onScroll={handleListScroll}
                style={
                  isMobile
                    ? { width: '100%', maxWidth: 480, margin: '0 auto', minHeight: 0, overflowY: 'auto', overscrollBehavior: 'contain', padding: '12px 24px 0' }
                    : { width: listWidth, flexShrink: 0, minHeight: 0, overflowY: 'auto', overscrollBehavior: 'contain', padding: '8px 0' }
                }
              >
                <AnimatePresence initial={false}>
                  {conversations.map((conversation) => {
                    const active = selectedConversation?.id === conversation.id;
                    return (
                      <motion.div
                        key={conversation.id}
                        layout
                        initial={false}
                        exit={{ opacity: 0, height: 0, marginBottom: 0 }}
                        transition={{ duration: 0.22, ease: 'easeInOut' }}
                        style={{ overflow: 'hidden' }}
                      >
                        {isMobile ? (
                          // Same hairline-divided-row pattern as ProfileMobile's
                          // ProfileMenuRow/SettingsListRow (icon/avatar + label +
                          // trailing chevron, no card background/border), reused
                          // here instead of the desktop rounded-card row so the
                          // whole Perfil area (Profile, Settings, now Mensajes)
                          // reads as one consistent list style on mobile. Wrapped
                          // in SwipeToDeleteRow for the swipe-left-to-delete
                          // gesture -- desktop has no equivalent yet (mouse
                          // hover, not touch-swipe, would be the natural
                          // pattern there; out of scope for now).
                          <SwipeToDeleteRow
                            isOpen={openSwipeId === conversation.id}
                            onOpenChange={(open) => setOpenSwipeId(open ? conversation.id : null)}
                            onDeleteClick={() => {
                              setOpenSwipeId(null);
                              setHideTarget(conversation);
                            }}
                            deleteLabel={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_MESSAGES_HIDE_ACTION)}
                          >
                            <button
                              type="button"
                              onClick={() => handleSelectConversation(conversation)}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 12,
                                width: '100%',
                                padding: '14px 4px',
                                border: 'none',
                                borderBottom: `1px solid ${token.colorBorderSecondary}`,
                                background: 'none',
                                textAlign: 'left',
                                cursor: 'pointer',
                              }}
                            >
                              <Badge count={conversation.unreadCount} size="small">
                                <Avatar style={{ background: token.colorFillTertiary }} />
                              </Badge>
                              <div style={{ minWidth: 0, flex: 1 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                                  <Text strong style={{ flex: 1, minWidth: 0, fontSize: 15, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {conversationTitle(conversation)}
                                  </Text>
                                  {/* Same icon-only status badge as the desktop row below --
                                      kept as a duplicated IIFE rather than a shared helper
                                      since the two rows render it at different sizes/positions. */}
                                  {(() => {
                                    const { Icon: StatusIcon, labelKey, bg, fg } = conversationStatusBadge(conversation, token);
                                    return (
                                      <span
                                        title={translate(t, labelKey)}
                                        style={{
                                          flexShrink: 0,
                                          width: 18,
                                          height: 18,
                                          borderRadius: '50%',
                                          background: bg,
                                          color: fg,
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                        }}
                                      >
                                        <StatusIcon size={11} />
                                      </span>
                                    );
                                  })()}
                                </div>
                                <Text type="secondary" style={{ fontSize: 12, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {conversation.kind === 'ServiceRequest' ? translate(t, TRANSLATIONS.MESSAGES_QUOTE_REQUEST_SUBTITLE) : conversationSubtitle(conversation, i18n.language)}
                                </Text>
                                <Text type="secondary" style={{ fontSize: 12, display: 'block', opacity: 0.75, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {conversation.lastMessageText || '—'}
                                </Text>
                                {conversation.lastMessageAt && (
                                  <Text type="secondary" style={{ fontSize: 11 }}>
                                    {dayjs(conversation.lastMessageAt).format('DD/MM HH:mm')}
                                  </Text>
                                )}
                              </div>
                              <IconChevronRight size={16} color={token.colorTextTertiary} />
                            </button>
                          </SwipeToDeleteRow>
                        ) : (
                          <div
                            onClick={() => handleSelectConversation(conversation)}
                            style={{
                              display: 'flex',
                              gap: 10,
                              alignItems: 'flex-start',
                              padding: '12px 14px',
                              margin: '2px 8px',
                              borderRadius: 14,
                              cursor: 'pointer',
                              background: active ? token.colorBgContainer : 'transparent',
                              border: `1px solid ${active ? token.colorBorder : 'transparent'}`,
                              transition: 'background 150ms ease, border-color 150ms ease',
                            }}
                          >
                            <Badge count={conversation.unreadCount} size="small">
                              <Avatar style={{ background: token.colorFillTertiary }} />
                            </Badge>
                            <div style={{ minWidth: 0, flex: 1 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                                <Text
                                  strong
                                  style={{ flex: 1, minWidth: 0, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                >
                                  {conversationTitle(conversation)}
                                </Text>
                                {/* Icon-only (title carries the label via the
                                    native tooltip) -- a text pill here would
                                    regularly force the title to truncate harder
                                    than it needs to at 375px, since this row's
                                    width is already tight with the unread count
                                    badge and the timestamp competing for it. */}
                                {(() => {
                                  const { Icon: StatusIcon, labelKey, bg, fg } = conversationStatusBadge(conversation, token);
                                  return (
                                    <span
                                      title={translate(t, labelKey)}
                                      style={{
                                        flexShrink: 0,
                                        width: 18,
                                        height: 18,
                                        borderRadius: '50%',
                                        background: bg,
                                        color: fg,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                      }}
                                    >
                                      <StatusIcon size={11} />
                                    </span>
                                  );
                                })()}
                              </div>
                              <Text type="secondary" style={{ fontSize: 12, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {conversation.kind === 'ServiceRequest' ? translate(t, TRANSLATIONS.MESSAGES_QUOTE_REQUEST_SUBTITLE) : conversationSubtitle(conversation, i18n.language)}
                              </Text>
                              <Text type="secondary" style={{ fontSize: 12, display: 'block', opacity: 0.75, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {conversation.lastMessageText || '—'}
                              </Text>
                              {conversation.lastMessageAt && (
                                <Text type="secondary" style={{ fontSize: 11 }}>
                                  {dayjs(conversation.lastMessageAt).format('DD/MM HH:mm')}
                                </Text>
                              )}
                            </div>
                          </div>
                        )}
                      </motion.div>
                    );
                  })}
                </AnimatePresence>
                {loadingMore && (
                  <div style={{ display: 'flex', justifyContent: 'center', padding: '12px 0' }}>
                    <Spin size="small" />
                  </div>
                )}
              </div>
            )}

            {!isMobile && (
              <div
                onMouseDown={handleResizerMouseDown}
                onMouseEnter={() => setResizerHover(true)}
                onMouseLeave={() => setResizerHover(false)}
                style={{
                  width: 5,
                  flexShrink: 0,
                  cursor: 'col-resize',
                  background: resizerHover ? token.colorBorder : token.colorBorderSecondary,
                  transition: 'background 150ms ease',
                }}
              />
            )}

            {(!isMobile || !!selectedConversation) &&
              (!selectedConversation ? (
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Text type="secondary">{translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_MESSAGES_SELECT_CONVERSATION_PROMPT)}</Text>
                </div>
              ) : bookingLoading ? (
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Spin />
                </div>
              ) : (
                <>
                  <MessageThreadPanel
                    conversation={selectedConversation}
                    booking={selectedBooking}
                    onOpenServiceRequest={
                      selectedConversation.serviceRequestId
                        ? () => navigate(`/heavy-duty-machinery/service-requests/${selectedConversation.serviceRequestId}`)
                        : undefined
                    }
                    detailsPanelOpen={detailsPanelOpen}
                    onToggleDetailsPanel={() => setDetailsPanelOpen(true)}
                    onViewAddress={() => setAddressModalOpen(true)}
                    canCancel={canCancelSelected}
                    cancelling={cancelling}
                    onCancel={handleCancelBooking}
                    onAcceptQuote={handleAcceptQuote}
                    acceptingQuote={acceptingQuote}
                    onCancelQuote={isGuestOfSelected ? handleCancelQuote : undefined}
                    cancellingQuote={cancellingQuote}
                    onProposeQuote={isGuestOfSelected ? undefined : () => setProposeQuoteOpen(true)}
                    onRejectQuote={isGuestOfSelected ? undefined : () => setRejectQuoteOpen(true)}
                    onHostCancelBooking={isGuestOfSelected ? undefined : () => setHostCancelOpen(true)}
                    onPayNow={isGuestOfSelected ? () => setPayNowOpen(true) : undefined}
                    onBack={isMobile ? handleBackToList : undefined}
                    showHeaderDetailsToggle={!isDesktop}
                    onMessagesRead={handleMessagesRead}
                  />
                  {/* Desktop only: permanent third column, fixed width --
                      unlike the list on the left, this one is never
                      wired to the resizer. */}
                  {isDesktop && detailsPanelOpen && selectedBooking && (
                    <div style={{ width: 260, flexShrink: 0, borderLeft: `1px solid ${token.colorBorderSecondary}`, height: '100%' }}>
                      <BookingDetailsPanel
                        booking={selectedBooking}
                        onClose={() => setDetailsPanelOpen(false)}
                        canCancel={canCancelSelected}
                        cancelling={cancelling}
                        onCancel={handleCancelBooking}
                      />
                    </div>
                  )}
                </>
              ))}
          </div>
        )}
        </div>

        {selectedBooking && payNowOpen && (
          <ResumePaymentModal bookingId={selectedBooking.id} open onClose={() => setPayNowOpen(false)} />
        )}

        {selectedBooking && hostCancelOpen && (
          <HostCancelBookingModal
            bookingId={selectedBooking.id}
            open
            onClose={() => setHostCancelOpen(false)}
            onCancelled={(updated) => {
              setSelectedBooking(updated);
              setHostCancelOpen(false);
            }}
          />
        )}
        {selectedBooking && proposeQuoteOpen && (
          <ProposeNewQuoteAmountModal
            booking={selectedBooking}
            open
            onClose={() => setProposeQuoteOpen(false)}
            onProposed={setSelectedBooking}
          />
        )}

        {selectedBooking && rejectQuoteOpen && (
          <RejectQuoteModal
            bookingId={selectedBooking.id}
            open
            onClose={() => setRejectQuoteOpen(false)}
            onRejected={setSelectedBooking}
          />
        )}

        {/* Tablet/mobile only: details open as an overlay instead of a
            permanent column, so the thread never permanently loses that
            width just because the guest might look at the booking info. */}
        {!isDesktop && (
          <Drawer
            placement="right"
            width={isMobile ? '100%' : 320}
            open={detailsPanelOpen && !!selectedBooking}
            onClose={() => setDetailsPanelOpen(false)}
            closable={false}
            styles={{ body: { padding: 0 } }}
          >
            {selectedBooking && (
              <BookingDetailsPanel
                booking={selectedBooking}
                onClose={() => setDetailsPanelOpen(false)}
                canCancel={canCancelSelected}
                cancelling={cancelling}
                onCancel={handleCancelBooking}
              />
            )}
          </Drawer>
        )}

        {selectedBooking && (
          <AddressMapModal booking={selectedBooking} open={addressModalOpen} onClose={() => setAddressModalOpen(false)} />
        )}

        <ConfirmBottomSheet
          open={!!hideTarget}
          title={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_MESSAGES_HIDE_CONFIRM_TITLE)}
          description={hideTarget && conversationSubtitle(hideTarget, i18n.language)}
          cancelText={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_MESSAGES_HIDE_CANCEL_BUTTON)}
          confirmText={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_MESSAGES_HIDE_CONFIRM_BUTTON)}
          onConfirm={handleConfirmHide}
          onClose={() => setHideTarget(undefined)}
        />
      </div>
    </AppLayout>
  );
};

export default Messages;
